import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { isUndefined } from 'lodash';
import { useNavigate } from 'react-router-dom';
import validate from 'validate.js';
import { FormState } from 'services/hooks/useFormValidation';
import { SignInProps } from './SignIn.props';
import { NameText, Root, TextFieldStyled } from './SignIn.styles';
import {
  ContentBody,
  ContentGrid,
  Form,
  GridContainer,
  Quote,
  QuoteContainer,
  QuoteInner,
  QuoteText,
  SignInButton,
  Title,
} from './SignIn.styles';
import { withRouter, WithRouterProps } from '../../services/hooks/with-router/withRouter';

const schema = {
  email: {
    presence: { allowEmpty: false, message: 'is required' },
    email: true,
    length: { maximum: 64 },
  },
  password: {
    presence: { allowEmpty: false, message: 'is required' },
    length: { maximum: 128 },
  },
};

const SignIn = (props: SignInProps & WithRouterProps) => {
  const navigate = useNavigate();

  const [formState, setFormState] = useState<FormState>({
    isValid: false,
    values: {},
    touched: {},
    errors: {},
  });

  useEffect(() => {
    const errors = validate(formState.values, schema);
    setFormState(currentFormState => ({
      ...currentFormState,
      isValid: isUndefined(errors),
      errors: errors ?? {},
    }));
  }, [formState.values]);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.persist();
    setFormState(currentFormState => ({
      ...currentFormState,
      values: {
        ...currentFormState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value,
      },
      touched: {
        ...currentFormState.touched,
        [event.target.name]: true,
      },
    }));
  };

  const handleSignIn = (event: FormEvent) => {
    event.preventDefault();
    navigate('/firm');
  };

  const hasError = (field: keyof typeof formState.values) =>
    formState.touched[field as keyof typeof formState.touched]
    && formState.errors[field as keyof typeof formState.errors];

  return (
    <Root>
      <GridContainer container>
        <QuoteContainer size={{ lg: 6 }}>
          <Quote>
            <QuoteInner>
              <QuoteText variant="h1">The Leading Independent</QuoteText>
              <div>
                <NameText variant="body1">Allocation Firm</NameText>
              </div>
            </QuoteInner>
          </Quote>
        </QuoteContainer>
        <ContentGrid size={{ lg: 6, xs: 12 }}>
          <ContentBody>
            <Form onSubmit={handleSignIn}>
              <Title variant="h2">Login</Title>
              <TextFieldStyled
                fullWidth
                label="Email address"
                name="email"
                onChange={handleChange}
                type="text"
                value={formState.values.email || ''}
              />
              <TextFieldStyled
                error={Boolean(hasError('password'))}
                fullWidth
                helperText={
                  hasError('password') ? formState.errors['password' as keyof typeof formState.errors][0] : null
                }
                label="Password"
                name="password"
                onChange={handleChange}
                type="password"
                value={formState.values.password || ''}
              />
              <SignInButton color="primary" disabled={!formState.isValid} fullWidth size="large" type="submit">
                Login
              </SignInButton>
            </Form>
          </ContentBody>
        </ContentGrid>
      </GridContainer>
    </Root>
  );
};

export default withRouter(SignIn);
